/**
 * SPL TOKEN TRANSFER SERVICE
 * 
 * Handles $SLAP token transfers to Treasury for game entry fees.
 * All game entries require a REAL blockchain transaction.
 * 
 * CRITICAL FIX: Use backend RPC proxy for ALL RPC calls to avoid CORS/rate-limiting
 */

import { 
  PublicKey, 
  Transaction,
  Keypair
} from '@solana/web3.js';
import {
  getAssociatedTokenAddress,
  createTransferInstruction,
  createAssociatedTokenAccountInstruction,
  TOKEN_PROGRAM_ID,
  ASSOCIATED_TOKEN_PROGRAM_ID
} from '@solana/spl-token';
import bs58 from 'bs58';
import axios from 'axios';

// ============================================================
// CONFIGURATION - HARDCODED FOR SECURITY
// ============================================================
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const RPC_PROXY_URL = `${BACKEND_URL}/api/solana/rpc`;
const SLAP_TOKEN_MINT = 'A7rKF3srg28UArWp1ibVVUbQmnjUnitTtq7ZFR4tpump';
const TREASURY_WALLET = 'GntfPdLmhjJ2unKJ9fp7S6rDz7AA1Hkn52pKpd9TceCa';
const SLAP_DECIMALS = 6; // $SLAP has 6 decimals

// ============================================================
// RPC HELPER FUNCTIONS - Use backend proxy for all RPC calls
// ============================================================

/**
 * Get latest blockhash via backend RPC proxy
 */
const getLatestBlockhashViaProxy = async () => {
  try {
    const response = await axios.post(RPC_PROXY_URL, {
      jsonrpc: "2.0",
      id: 1,
      method: "getLatestBlockhash",
      params: [{ commitment: "confirmed" }]
    });
    
    if (response.data?.result?.value) {
      return {
        blockhash: response.data.result.value.blockhash,
        lastValidBlockHeight: response.data.result.value.lastValidBlockHeight
      };
    }
    throw new Error("Invalid blockhash response");
  } catch (error) {
    console.error('[TokenTransfer] Failed to get blockhash via proxy:', error);
    throw error;
  }
};

/**
 * Send raw transaction via backend RPC proxy
 */
const sendRawTransactionViaProxy = async (serializedTransaction) => {
  try {
    const base64Tx = Buffer.from(serializedTransaction).toString('base64');
    
    const response = await axios.post(RPC_PROXY_URL, {
      jsonrpc: "2.0",
      id: 1,
      method: "sendTransaction",
      params: [
        base64Tx,
        {
          encoding: "base64",
          skipPreflight: false,
          preflightCommitment: "confirmed",
          maxRetries: 3
        }
      ]
    });
    
    if (response.data?.error) {
      throw new Error(response.data.error.message || "Transaction send failed");
    }
    
    return response.data?.result; // Returns signature
  } catch (error) {
    console.error('[TokenTransfer] Failed to send transaction:', error);
    throw error;
  }
};

/**
 * Confirm transaction via backend RPC proxy
 */
const confirmTransactionViaProxy = async (signature, blockhash, lastValidBlockHeight) => {
  try {
    // Poll for transaction status
    let confirmed = false;
    let attempts = 0;
    const maxAttempts = 30;
    
    while (!confirmed && attempts < maxAttempts) {
      const response = await axios.post(RPC_PROXY_URL, {
        jsonrpc: "2.0",
        id: 1,
        method: "getSignatureStatuses",
        params: [[signature], { searchTransactionHistory: true }]
      });
      
      const status = response.data?.result?.value?.[0];
      
      if (status) {
        if (status.err) {
          throw new Error(`Transaction failed: ${JSON.stringify(status.err)}`);
        }
        if (status.confirmationStatus === 'confirmed' || status.confirmationStatus === 'finalized') {
          confirmed = true;
          break;
        }
      }
      
      // Wait 1 second before checking again
      await new Promise(resolve => setTimeout(resolve, 1000));
      attempts++;
    }
    
    if (!confirmed) {
      throw new Error("Transaction confirmation timeout");
    }
    
    return { confirmed: true };
  } catch (error) {
    console.error('[TokenTransfer] Confirmation failed:', error);
    throw error;
  }
};

/**
 * Check if a token account exists via backend proxy
 */
const checkAccountExistsViaProxy = async (accountAddress) => {
  try {
    const response = await axios.post(RPC_PROXY_URL, {
      jsonrpc: "2.0",
      id: 1,
      method: "getAccountInfo",
      params: [accountAddress, { encoding: "jsonParsed" }]
    });
    
    return response.data?.result?.value !== null;
  } catch (error) {
    console.error('[TokenTransfer] Account check failed:', error);
    return false;
  }
};

/**
 * Transfer $SLAP tokens to Treasury (Entry Fee)
 * 
 * CRITICAL: Uses backend RPC proxy for ALL RPC calls to avoid CORS issues
 * 
 * @param {number} amount - Amount of $SLAP tokens to transfer (in UI units, not lamports)
 * @param {string} userPublicKey - User's wallet public key
 * @param {string} secretKeyBase58 - User's secret key in Base58
 * @param {function} onProgress - Progress callback
 * @returns {Promise<{success: boolean, signature?: string, error?: string}>}
 */
export const transferToTreasury = async ({
  amount,
  userPublicKey,
  secretKeyBase58,
  onProgress = () => {}
}) => {
  try {
    onProgress({ step: 'connecting', message: 'Connecting to Solana...' });
    
    // Create keypair from secret key
    const secretKeyBytes = bs58.decode(secretKeyBase58);
    const userKeypair = Keypair.fromSecretKey(secretKeyBytes);
    
    // Verify public key matches
    if (userKeypair.publicKey.toString() !== userPublicKey) {
      throw new Error('Secret key does not match public key');
    }
    
    onProgress({ step: 'preparing', message: 'Preparing transfer...' });
    
    // Get token mint
    const tokenMint = new PublicKey(SLAP_TOKEN_MINT);
    const treasuryPubkey = new PublicKey(TREASURY_WALLET);
    
    // Get Associated Token Accounts (ATAs)
    const userATA = await getAssociatedTokenAddress(
      tokenMint,
      userKeypair.publicKey
    );
    
    const treasuryATA = await getAssociatedTokenAddress(
      tokenMint,
      treasuryPubkey
    );
    
    // Convert amount to token units (with decimals)
    const amountInSmallestUnit = Math.floor(amount * Math.pow(10, SLAP_DECIMALS));
    
    console.log(`[TokenTransfer] Transferring ${amount} $SLAP to Treasury`);
    console.log(`[TokenTransfer] User ATA: ${userATA.toString()}`);
    console.log(`[TokenTransfer] Treasury ATA: ${treasuryATA.toString()}`);
    console.log(`[TokenTransfer] Amount (smallest unit): ${amountInSmallestUnit}`);
    
    onProgress({ step: 'building', message: 'Building transaction...' });
    
    // Build transaction with instructions
    const transaction = new Transaction();
    
    // Check if Treasury ATA exists - if not, we need to create it
    const treasuryATAExists = await checkAccountExistsViaProxy(treasuryATA.toString());
    console.log(`[TokenTransfer] Treasury ATA exists: ${treasuryATAExists}`);
    
    if (!treasuryATAExists) {
      console.log('[TokenTransfer] Creating Treasury ATA...');
      // Add instruction to create Treasury ATA (user pays for this)
      const createATAInstruction = createAssociatedTokenAccountInstruction(
        userKeypair.publicKey,    // Payer
        treasuryATA,              // Associated Token Account to create
        treasuryPubkey,           // Owner of the ATA
        tokenMint,                // Token Mint
        TOKEN_PROGRAM_ID,
        ASSOCIATED_TOKEN_PROGRAM_ID
      );
      transaction.add(createATAInstruction);
    }
    
    // Check if User ATA exists
    const userATAExists = await checkAccountExistsViaProxy(userATA.toString());
    console.log(`[TokenTransfer] User ATA exists: ${userATAExists}`);
    
    if (!userATAExists) {
      throw new Error('User does not have a $SLAP token account. Please get some $SLAP tokens first.');
    }
    
    // Create transfer instruction
    const transferInstruction = createTransferInstruction(
      userATA,
      treasuryATA,
      userKeypair.publicKey,
      amountInSmallestUnit,
      [],
      TOKEN_PROGRAM_ID
    );
    
    transaction.add(transferInstruction);
    
    // CRITICAL FIX: Get recent blockhash via backend proxy (avoids CORS)
    const { blockhash, lastValidBlockHeight } = await getLatestBlockhashViaProxy();
    transaction.recentBlockhash = blockhash;
    transaction.feePayer = userKeypair.publicKey;
    
    onProgress({ step: 'signing', message: 'Signing transaction...' });
    
    // Sign transaction
    transaction.sign(userKeypair);
    
    onProgress({ step: 'sending', message: 'Sending to blockchain...' });
    
    // CRITICAL FIX: Send transaction via backend proxy (avoids CORS)
    const signature = await sendRawTransactionViaProxy(transaction.serialize());
    
    console.log(`[TokenTransfer] Transaction sent: ${signature}`);
    
    onProgress({ step: 'confirming', message: 'Confirming transaction...' });
    
    // CRITICAL FIX: Confirm via backend proxy (avoids CORS)
    await confirmTransactionViaProxy(signature, blockhash, lastValidBlockHeight);
    
    console.log(`[TokenTransfer] ✅ Transfer confirmed! Signature: ${signature}`);
    
    return {
      success: true,
      signature,
      amount,
      treasury: TREASURY_WALLET
    };
    
  } catch (error) {
    console.error('[TokenTransfer] ❌ Transfer failed:', error);
    return {
      success: false,
      error: error.message || 'Transfer failed'
    };
  }
};

/**
 * Check if user has enough $SLAP for entry fee (via backend proxy)
 */
export const checkSlapBalance = async (userPublicKey, requiredAmount) => {
  try {
    // Use backend proxy to bypass CORS
    const response = await axios.post(RPC_PROXY_URL, {
      jsonrpc: "2.0",
      id: 1,
      method: "getTokenAccountsByOwner",
      params: [
        userPublicKey,
        { mint: SLAP_TOKEN_MINT },
        { encoding: "jsonParsed", commitment: "confirmed" }
      ]
    });
    
    let balance = 0;
    
    if (response.data?.result?.value && response.data.result.value.length > 0) {
      for (const account of response.data.result.value) {
        const tokenAmount = account.account?.data?.parsed?.info?.tokenAmount;
        if (tokenAmount) {
          balance += parseFloat(tokenAmount.uiAmount || 0);
        }
      }
    }
    
    console.log(`[TokenTransfer] Balance check: ${balance} $SLAP (need ${requiredAmount})`);
    
    return {
      hasEnough: balance >= requiredAmount,
      balance,
      required: requiredAmount
    };
  } catch (error) {
    console.error('[TokenTransfer] Balance check failed:', error);
    return {
      hasEnough: false,
      balance: 0,
      required: requiredAmount,
      error: error.message
    };
  }
};

export default {
  transferToTreasury,
  checkSlapBalance,
  TREASURY_WALLET,
  SLAP_TOKEN_MINT
};
